// AICA Radio Component - Meridian Dark Design System
// Based on Radix UI Radio Group primitive
// Executive style: Professional teal with refined indicators

import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

// RadioGroup Root Component
const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('grid gap-2', className)}
      {...props}
      ref={ref}
    />
  );
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

// RadioGroupItem Component
export interface RadioGroupItemProps extends React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
> {
  /**
   * Size variant
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Error state
   */
  error?: boolean;
}

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, size = 'md', error, ...props }, ref) => {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const dotSizes = {
    sm: 'h-1.5 w-1.5',
    md: 'h-2.5 w-2.5',
    lg: 'h-3 w-3',
  };

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        // Base styles - Meridian Dark
        'aspect-square rounded-full border',
        'bg-bg-surface', // Surface background when unchecked
        'border-border', // Subtle border

        // Focus states - Meridian teal ring
        'focus:outline-none',
        'focus-visible:ring-2 focus-visible:ring-primary/30',
        'focus-visible:ring-offset-2 focus-visible:ring-offset-bg',

        // Checked state - Teal border with inner dot
        'data-[state=checked]:border-primary',
        'data-[state=checked]:border-2',
        'data-[state=checked]:shadow-glow-subtle',

        // Hover state
        'hover:border-border',
        'hover:shadow-sm',

        // Error state
        error && [
          'border-danger',
          'data-[state=checked]:border-danger',
          'focus-visible:ring-danger/30',
        ],

        // Disabled state
        'disabled:cursor-not-allowed',
        'disabled:opacity-50',
        'disabled:bg-bg-subtle',

        // Transitions
        'transition-all duration-150 ease-standard',
        'motion-reduce:transition-none',

        // Size variant
        sizes[size],

        className
      )}
      aria-invalid={error ? 'true' : undefined}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle
          className={cn(
            'fill-primary text-primary',
            error && 'fill-danger text-danger',
            // Animate the dot appearance
            'animate-in zoom-in-95 duration-150 ease-emphasized',
            dotSizes[size]
          )}
        />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export type RadioGroupProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Root
>;
export { RadioGroup, RadioGroupItem };
